import { visit } from 'unist-util-visit';
import type { Element, Root, RootContent } from 'hast';

interface Options {
  enableFootnotes: boolean;
}

/**
 * `data-footnotes` survives the `rehype-raw` round-trip as an empty string, so
 * presence -- not truthiness -- is the reliable signal.
 */
export const hasFootnoteMarker = (node: Element, attribute: string): boolean => {
  const properties = node.properties as Record<string, unknown> | undefined;
  return properties !== undefined && attribute in properties;
};

const isFootnoteSection = (node: RootContent): node is Element =>
  node.type === 'element' && node.tagName === 'section' && hasFootnoteMarker(node, 'dataFootnotes');

/**
 * Applies the feature switches that have to act on the tree rather than on the
 * plugin list, because `remark-gfm` has no per-construct toggles.
 */
export default function rehypeFeatureGates(options: Options) {
  return (tree: Root) => {
    if (options.enableFootnotes) return;

    tree.children = tree.children.filter((child) => !isFootnoteSection(child));

    // Turn the now-dangling reference superscripts back into plain markers.
    visit(tree, 'element', (node: Element) => {
      if (node.tagName !== 'a') return;
      if (!hasFootnoteMarker(node, 'dataFootnoteRef')) return;
      node.tagName = 'span';
      node.properties = { className: ['footnote-ref-disabled'] };
    });
  };
}
